import { Link, useLocation } from 'react-router-dom'
import { X, MapPin, Phone, ChevronDown, User, Calendar, LogOut, HelpCircle, Landmark, Home, BedDouble, Package, Store, BookOpen, Image, Mail } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useUser } from '../context/UserContext'

const navLinks = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/rooms', label: 'Rooms', icon: BedDouble },
  { to: '/manauna-dham', label: 'Manauna Dham', icon: Landmark, highlight: true },
  { to: '/services', label: 'Services', icon: Package },
  { to: '/packages', label: 'Packages', icon: Package },
  { to: '/vendors', label: 'Vendors', icon: Store },
  { to: '/blogs', label: 'Blogs', icon: BookOpen },
  { to: '/gallery', label: 'Gallery', icon: Image },
  { to: '/contact', label: 'Contact', icon: Mail },
]

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { userDetails, logout } = useUser()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (isMenuOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [isMenuOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }
    if (isUserMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isUserMenuOpen])

  const getUserInitials = () => {
    if (userDetails?.displayName) {
      const names = userDetails.displayName.split(' ')
      return names.length >= 2
        ? `${names[0][0]}${names[1][0]}`.toUpperCase()
        : userDetails.displayName.substring(0, 2).toUpperCase()
    }
    if (userDetails?.email) return userDetails.email.substring(0, 2).toUpperCase()
    if (userDetails?.phoneNumber) return userDetails.phoneNumber.slice(-2)
    return 'U'
  }

  const handleLogout = () => { logout(); setIsUserMenuOpen(false) }

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
        {/* Top contact strip — hidden on mobile */}
        <div className="hidden md:block bg-secondary-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-10 text-sm">
              <div className="flex items-center gap-5">
                <a href="#" className="flex items-center gap-1.5 text-white/80 hover:text-primary-400 transition-colors">
                  <MapPin className="h-3.5 w-3.5 text-primary-400" />
                  Manona Dham, Aonla
                </a>
                <a href="tel:9457425914" className="flex items-center gap-1.5 text-white/80 hover:text-primary-400 transition-colors">
                  <Phone className="h-3.5 w-3.5 text-primary-400" />
                  9457425914
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative group">
                  <button className="bg-primary-500 text-white px-3.5 py-1.5 rounded-md text-xs font-semibold hover:bg-primary-600 transition-colors flex items-center gap-1">
                    Become a host
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-xl border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <a href="/vendor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">Host Login</a>
                    <Link to="/vendor/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg">Host Signup</Link>
                  </div>
                </div>
                {userDetails ? (
                  <div className="relative" ref={userMenuRef}>
                    <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-white/20 hover:border-primary-400 transition-all">
                      {userDetails.photoURL ? (
                        <img src={userDetails.photoURL} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold">{getUserInitials()}</div>
                      )}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden">
                        <div className="px-4 py-3 border-b bg-gray-50">
                          <p className="text-sm font-semibold text-gray-900 truncate">{userDetails.displayName || 'User'}</p>
                          {userDetails.email && <p className="text-xs text-gray-500 truncate mt-0.5">{userDetails.email}</p>}
                        </div>
                        <div className="py-1">
                          <Link to="/bookings" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"><Calendar className="h-4 w-4 text-gray-400" />My Bookings</Link>
                          <Link to="/profile" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"><User className="h-4 w-4 text-gray-400" />Profile</Link>
                          <Link to="/contact" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"><HelpCircle className="h-4 w-4 text-gray-400" />Contact Us</Link>
                          <div className="border-t my-1" />
                          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" />Logout</button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative group">
                    <button className="border border-primary-400/60 text-primary-300 px-3.5 py-1.5 rounded-md text-xs font-semibold hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-colors flex items-center gap-1">
                      Log in / Sign up
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-xl border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <Link to="/auth/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">Login</Link>
                      <Link to="/user/signup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg">Signup</Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main nav bar */}
        <div className={`bg-white/95 backdrop-blur-md border-b border-gray-100 transition-all duration-300 ${isScrolled ? 'py-2' : 'py-3 md:py-4'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link to="/" className="shrink-0">
                <img
                  src="https://www.aonlastay.napps.in/assets/img/67669fccd73b0.png"
                  alt="Aonla Stay"
                  className={`border-2 border-primary-500 rounded-md transition-all duration-300 ${isScrolled ? 'h-9' : 'h-10 md:h-12'}`}
                />
              </Link>

              {/* Desktop nav */}
              <nav className="hidden lg:flex items-center gap-1">
                {navLinks.map(link => {
                  const active = location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to))
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                        ${active ? 'text-primary-600 bg-primary-50' : 'text-gray-600 hover:text-primary-500 hover:bg-gray-50'}
                        ${link.highlight ? 'inline-flex items-center gap-1.5' : ''}`}
                    >
                      {link.highlight && <Landmark className="h-4 w-4 text-primary-500" />}
                      {link.label}
                    </Link>
                  )
                })}
              </nav>

              {/* Mobile right section */}
              <div className="flex items-center gap-3 lg:hidden">
                {/* Mobile user avatar or login */}
                {userDetails ? (
                  <Link to="/profile" className="shrink-0">
                    {userDetails.photoURL ? (
                      <img src={userDetails.photoURL} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-primary-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold ring-2 ring-primary-200">{getUserInitials()}</div>
                    )}
                  </Link>
                ) : (
                  <Link to="/auth/login" className="text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    Login
                  </Link>
                )}
                {/* Hamburger */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="relative w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                >
                  <div className="relative w-5 h-5">
                    <span className={`absolute left-0 w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? 'top-[9px] rotate-45' : 'top-1'}`} />
                    <span className={`absolute left-0 top-[9px] w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0 scale-0' : 'opacity-100'}`} />
                    <span className={`absolute left-0 w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? 'top-[9px] -rotate-45' : 'top-[17px]'}`} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile slide-out drawer */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-all duration-300 ${isMenuOpen ? 'visible' : 'invisible'}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Drawer */}
        <div className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-300 ease-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {/* Drawer header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-secondary-500 to-secondary-600">
            <img
              src="https://www.aonlastay.napps.in/assets/img/67669fccd73b0.png"
              alt="Aonla Stay"
              className="h-9 rounded-md"
            />
            <button onClick={() => setIsMenuOpen(false)} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center hover:bg-white/25 transition-colors">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>

          {/* User card */}
          {userDetails && (
            <div className="mx-4 mt-4 p-3.5 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100/50 border border-primary-200/50">
              <div className="flex items-center gap-3">
                {userDetails.photoURL ? (
                  <img src={userDetails.photoURL} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-primary-300" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold ring-2 ring-primary-300">{getUserInitials()}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{userDetails.displayName || 'User'}</p>
                  {userDetails.email && <p className="text-xs text-gray-500 truncate">{userDetails.email}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Nav links */}
          <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
            {navLinks.map((link, i) => {
              const Icon = link.icon
              const active = location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to))
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                    ${active ? 'bg-primary-50 text-primary-700 border border-primary-200/50' : 'text-gray-700 hover:bg-gray-50'}
                    ${link.highlight ? 'bg-gradient-to-r from-secondary-50 to-primary-50 border border-secondary-200/40' : ''}`}
                  style={{ animationDelay: `${i * 30}ms` }}
                >
                  <Icon className={`h-4.5 w-4.5 ${active ? 'text-primary-500' : link.highlight ? 'text-primary-500' : 'text-gray-400'}`} />
                  {link.label}
                  {link.highlight && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-primary-500 bg-primary-100 px-1.5 py-0.5 rounded-full">New</span>
                  )}
                </Link>
              )
            })}

            {/* Contact strip on mobile */}
            <div className="pt-3 mt-3 border-t border-gray-100 space-y-1">
              <a href="tel:9457425914" className="flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                <Phone className="h-4.5 w-4.5 text-primary-400" />
                Call: 9457425914
              </a>
              <a href="#" className="flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                <MapPin className="h-4.5 w-4.5 text-primary-400" />
                Manona Dham, Aonla
              </a>
            </div>

            {/* Action buttons */}
            <div className="pt-3 mt-1 border-t border-gray-100 space-y-2">
              {userDetails ? (
                <>
                  <Link to="/bookings" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">
                    <Calendar className="h-4 w-4 text-gray-400" />My Bookings
                  </Link>
                  <button onClick={() => { handleLogout(); setIsMenuOpen(false) }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50">
                    <LogOut className="h-4 w-4" />Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2">
                  <Link to="/auth/login" onClick={() => setIsMenuOpen(false)} className="flex-1 text-center py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">Login</Link>
                  <Link to="/vendor/signup" onClick={() => setIsMenuOpen(false)} className="flex-1 text-center py-3 rounded-xl border-2 border-secondary-300 text-secondary-700 font-semibold text-sm hover:bg-secondary-50 transition-colors">Host</Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}

export default Header
